import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { closeSchool, getToday } from "../../../service/operatingDay";
import { selectUser } from "../../../core/data/redux/slice/user";
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice";
import { canAccess, isSuperAdmin } from "../../../utils/rbacHelper";
import { all_routes } from "../../router/all_routes";

// Nag interval — the pop-up re-checks every 15 minutes while today is PENDING.
const POLL_MS = 15 * 60 * 1000;

// Who gets nagged to open the school. Receptionists (front-desk) of the campus.
const isReceptionist = (roleName?: string) => {
  const r = (roleName || "").toLowerCase();
  return r.includes("reception") || r.includes("front desk");
};

// Global reminder: for a campus receptionist, while today's status is PENDING
// (a fresh Karachi day nobody has opened/closed yet), pop up every 15 min until
// they act. Because PENDING only exists once the Karachi day has started, this
// naturally only fires after midnight, as required.
const SchoolOpeningReminder: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const currentSession = useSelector(selectCurrentSession);
  // Only nag receptionists who can actually open the school — otherwise the
  // "Open the school" button would land on /unauthorized. Grant the receptionist
  // role "school-opening.view" (per campus) for the pop-up to appear.
  const eligible =
    isReceptionist(user?.role?.name) &&
    (isSuperAdmin(user) || canAccess("school-opening.view", user));

  const [show, setShow] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [closing, setClosing] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const check = useCallback(async () => {
    try {
      const res = await getToday();
      setShow(res.status === "PENDING");
    } catch {
      /* transient — try again next tick */
    }
  }, []);

  useEffect(() => {
    if (!eligible) return;
    check();
    timer.current = setInterval(check, POLL_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [eligible, check]);

  const handleMarkClosed = async () => {
    setClosing(true);
    try {
      await closeSchool({ sessionId: currentSession?._id });
      toast.success("School marked closed for today.");
      setConfirmingClose(false);
      setShow(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || "Failed to mark closed");
    } finally {
      setClosing(false);
    }
  };

  if (!eligible || !show) return null;

  return (
    <div className="modal fade show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          {confirmingClose ? (
            <>
              <div className="modal-header">
                <h5 className="modal-title text-danger">
                  <i className="ti ti-lock me-1" /> Confirm school closed
                </h5>
              </div>
              <div className="modal-body">
                <p className="mb-0">
                  Are you sure today's school should be marked closed? No staff or classes will be
                  opened for today.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  disabled={closing}
                  onClick={() => setConfirmingClose(false)}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={closing}
                  onClick={handleMarkClosed}
                >
                  <i className="ti ti-lock me-1" /> Yes, mark closed
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="modal-header">
                <h5 className="modal-title text-warning">
                  <i className="ti ti-clock me-1" /> School opening pending
                </h5>
              </div>
              <div className="modal-body">
                <p className="mb-0">
                  Today's school opening for your campus hasn't been set yet. Please open the school for
                  today's staff and classes, or mark it closed.
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light me-2" onClick={() => setShow(false)}>
                  Remind me later
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger me-2"
                  onClick={() => setConfirmingClose(true)}
                >
                  <i className="ti ti-lock me-1" /> Mark as Closed
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShow(false);
                    navigate(all_routes.schoolOpening);
                  }}
                >
                  Open the school
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchoolOpeningReminder;
